import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';

const CUSTOM_PREFIX = '__custom__:';

export default function CounterpartyCreatableSelect({
  value,
  onChange,
  onSelectItem,
  fetchOptions,
  minSearchLength = 2,
  placeholder = 'Search counterparty…',
  searchHint,
  disabled = false,
  allowClear = true,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);
  const fetchRef = useRef(fetchOptions);

  useEffect(() => {
    fetchRef.current = fetchOptions;
  }, [fetchOptions]);

  const runSearch = useCallback(
    async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < minSearchLength) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchRef.current(trimmed);
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

        const mapped = items.map((item) => ({
          value: item.uuid ?? item.id,
          label: item.label ?? item.name ?? String(item.id),
          item,
        }));

        const exactMatch = items.some(
          (item) => String(item.name || '').trim().toLowerCase() === trimmed.toLowerCase(),
        );

        if (!exactMatch) {
          mapped.unshift({
            value: `${CUSTOM_PREFIX}${trimmed}`,
            label: `Use "${trimmed}" (not in system)`,
            item: { custom: true, name: trimmed },
          });
        }

        setOptions(mapped);
      } catch {
        if (trimmed.length >= minSearchLength) {
          setOptions([
            {
              value: `${CUSTOM_PREFIX}${trimmed}`,
              label: `Use "${trimmed}" (not in system)`,
              item: { custom: true, name: trimmed },
            },
          ]);
        } else {
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [minSearchLength],
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, runSearch]);

  const handleChange = (nextValue, option) => {
    onChange?.(nextValue);
    const item = option?.item ?? null;
    if (item?.custom) {
      onSelectItem?.({ custom: true, name: item.name, uuid: null, id: null });
      return;
    }
    onSelectItem?.(item);
  };

  const notFoundContent = loading ? (
    <Spin size="small" />
  ) : search.trim().length < minSearchLength ? (
    searchHint || `Type at least ${minSearchLength} characters to search`
  ) : (
    'No results — keep typing to use a custom name'
  );

  return (
    <Select
      showSearch
      allowClear={allowClear}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      filterOption={false}
      onSearch={setSearch}
      loading={loading}
      disabled={disabled}
      options={options}
      notFoundContent={notFoundContent}
      className="w-full"
      style={{ width: '100%' }}
      optionFilterProp="label"
    />
  );
}
